import { queryOptions } from '@tanstack/react-query'
import {
  IssuerIntegratorListModel,
  IssuerIntegratorListResponse,
} from 'src/models/issuerIntegrator.models'
import {
  getIssuer,
  getIssuers,
} from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.network'
import { issuerQueryKeys } from 'src/queries/queries.constants'

/**
 * Issuer-integrator query keys.
 *
 * KEY REUSE: the root and detail shapes intentionally import
 * `issuerQueryKeys` from `queries.constants.ts`, the shared key root the
 * (now-deleted) legacy `useQueryIssuer` hook in
 * `src/queries/creditCard.queries.tsx` also cached under — both produce
 * `['issuers', issuerId]` for a single issuer. The `list` key is net-new
 * (`['issuers', 'list']`); it cannot collide with detail keys because issuer
 * ids are `ii_`-prefixed.
 */
export const issuerIntegratorQueryKeys = {
  all: () => issuerQueryKeys.all,
  list: () => [...issuerQueryKeys.all, 'list'] as const,
  detail: (issuerId: string | undefined) => issuerQueryKeys.detail(issuerId),
}

export const toIssuerIntegratorListModel = (
  data: IssuerIntegratorListResponse,
): IssuerIntegratorListModel => new IssuerIntegratorListModel(data)

/**
 * Cache behavior: app QueryClient defaults, no staleTime override. The Redux
 * thunks these replaced refetched on every consumer mount, and the legacy
 * `useQueryIssuer` cache on the same detail keys also ran on app defaults
 * (it never passed `queryCacheOptions`). A reference-data staleTime bump for
 * the issuer catalog would change behavior and belongs to a deliberate
 * follow-up, not this swap.
 */
export const issuerIntegratorQueries = {
  list: () =>
    queryOptions({
      queryKey: issuerIntegratorQueryKeys.list(),
      queryFn: () => getIssuers(),
    }),
  detail: (issuerId: string | undefined) =>
    queryOptions({
      queryKey: issuerIntegratorQueryKeys.detail(issuerId),
      queryFn: () => getIssuer(issuerId!),
      enabled: Boolean(issuerId),
    }),
}
